import { client } from '@api/rest/client';
import type { SubflowTemplateFormDTO } from '@conversed/rest/src/contracts/subflow-templates/subflow-templates.dto';
import ErrorCodes from '@conversed/shared/src/errors/error-codes';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  toastErrorTranslated,
  toastSuccessTranslated,
} from '../../helpers/toast';

export const useSubflowTemplateActions = () => {
  const dispatch = useDispatch();

  const deleteSubflowTemplate = useCallback(
    async (id: string) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        await client.subflowTemplates.deleteSubflowTemplate({
          params: { key: id },
        });

        dispatch(mainActions.deleteSubflowTemplate(id));

        toastSuccessTranslated('subflow-templates.delete.success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const createSubflowTemplate = useCallback(
    async (template: SubflowTemplateFormDTO) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const { data: templates } =
          await client.subflowTemplates.createSubflowTemplate({
            body: template,
          });

        toastSuccessTranslated('subflow-templates.create.success');

        dispatch(mainActions.setSubflowTemplates(templates));
      } catch (error: any) {
        console.error(error);

        if (error?.response?.data?.message === ErrorCodes.ALREADY_EXISTS) {
          toastErrorTranslated(
            'subflow-templates.duplicate-name-toast-message',
            {
              name: template.name,
            },
          );
        } else {
          toastErrorTranslated(error);
        }

        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const getSubflowTemplates = useCallback(async () => {
    try {
      dispatch(mainActions.setUIState({ isLoading: true }));
      const { data: subflowTemplates } =
        await client.subflowTemplates.getAllSubflowTemplates();
      return subflowTemplates;
    } catch (error) {
      console.error(error);
      toastErrorTranslated(error);
      return [];
    } finally {
      dispatch(mainActions.setUIState({ isLoading: false }));
    }
  }, [dispatch]);

  const fetchSubflowTemplates = useCallback(async () => {
    const templates = await getSubflowTemplates();

    dispatch(mainActions.setSubflowTemplates(templates));
  }, [getSubflowTemplates, dispatch]);

  return {
    fetchSubflowTemplates,
    getSubflowTemplates,
    deleteSubflowTemplate,
    createSubflowTemplate,
  };
};
